import { supabase } from "../supabaseClient";

export type Folder = {
  id: number;
  name: string;
  path: string;
  parent_path: string;
  created_at: string;
  updated_at: string;
};

export type FolderWithCounts = Folder & {
  totalFiles: number;
  usedFiles: number;
};

/**
 * Get all folders for the current user
 */
export async function getFolders(parentPath: string = ""): Promise<Folder[]> {
  const { data, error } = await supabase
    .from("folders")
    .select("*")
    .eq("parent_path", parentPath)
    .order("name");

  if (error) {
    throw error;
  }

  return data || [];
}

/**
 * Create a new folder
 */
export async function createFolder(
  name: string,
  parentPath: string = ""
): Promise<Folder> {
  const path = parentPath ? `${parentPath}/${name}` : name;

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw userError || new Error("User not authenticated");
  }

  const { data, error } = await supabase
    .from("folders")
    .insert({
      name,
      path,
      parent_path: parentPath,
      user_id: user.id,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Delete a folder (and all its contents)
 */
export async function deleteFolder(folderId: number): Promise<void> {
  const { error } = await supabase.from("folders").delete().eq("id", folderId);

  if (error) {
    throw error;
  }
}

/**
 * Rename a folder
 */
export async function renameFolder(
  folderId: number,
  newName: string
): Promise<Folder> {
  const { data: folder, error: fetchError } = await supabase
    .from("folders")
    .select("*")
    .eq("id", folderId)
    .single();

  if (fetchError) {
    throw fetchError;
  }

  const newPath = folder.parent_path
    ? `${folder.parent_path}/${newName}`
    : newName;

  // Update the folder itself
  const { data, error } = await supabase
    .from("folders")
    .update({
      name: newName,
      path: newPath,
    })
    .eq("id", folderId)
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  // Update all subfolders - find folders that start with the old path
  const { data: subfolders, error: subfoldersError } = await supabase
    .from("folders")
    .select("*")
    .like("path", `${folder.path}/%`);

  if (subfoldersError) {
    throw subfoldersError;
  }

  // Update each subfolder's path
  for (const subfolder of subfolders || []) {
    const newSubfolderPath = subfolder.path.replace(folder.path, newPath);
    const newSubfolderParentPath = newSubfolderPath.substring(
      0,
      newSubfolderPath.lastIndexOf("/")
    );

    const { error: subfolderError } = await supabase
      .from("folders")
      .update({
        path: newSubfolderPath,
        parent_path: newSubfolderParentPath,
      })
      .eq("id", subfolder.id);

    if (subfolderError) {
      throw subfolderError;
    }
  }

  // Update all files in this folder and subfolders
  const { error: filesError } = await supabase
    .from("media_metadata")
    .update({ folder_path: newPath })
    .eq("folder_path", folder.path);

  if (filesError) {
    throw filesError;
  }

  // Update files in subfolders
  for (const subfolder of subfolders || []) {
    const newSubfolderPath = subfolder.path.replace(folder.path, newPath);
    const { error: subfolderFilesError } = await supabase
      .from("media_metadata")
      .update({ folder_path: newSubfolderPath })
      .eq("folder_path", subfolder.path);

    if (subfolderFilesError) {
      throw subfolderFilesError;
    }
  }

  return data;
}

/**
 * Move files to a folder
 */
export async function moveFilesToFolder(
  filePaths: string[],
  folderPath: string
): Promise<void> {
  const { error } = await supabase
    .from("media_metadata")
    .update({ folder_path: folderPath })
    .in("path", filePaths);

  if (error) {
    throw error;
  }
}

/**
 * Move a folder and all its contents to a new parent folder
 */
export async function moveFolder(
  folderId: number,
  newParentPath: string
): Promise<void> {
  const { data: folder, error: fetchError } = await supabase
    .from("folders")
    .select("*")
    .eq("id", folderId)
    .single();

  if (fetchError) {
    throw fetchError;
  }

  const newPath = newParentPath
    ? `${newParentPath}/${folder.name}`
    : folder.name;

  // Update the folder itself
  const { error } = await supabase
    .from("folders")
    .update({
      path: newPath,
      parent_path: newParentPath,
    })
    .eq("id", folderId);

  if (error) {
    throw error;
  }

  // Update all subfolders - find folders that start with the old path
  const { data: subfolders, error: subfoldersError } = await supabase
    .from("folders")
    .select("*")
    .like("path", `${folder.path}/%`);

  if (subfoldersError) {
    throw subfoldersError;
  }

  // Update each subfolder's path
  for (const subfolder of subfolders || []) {
    const newSubfolderPath = subfolder.path.replace(folder.path, newPath);
    const newSubfolderParentPath = newSubfolderPath.substring(
      0,
      newSubfolderPath.lastIndexOf("/")
    );

    const { error: subfolderError } = await supabase
      .from("folders")
      .update({
        path: newSubfolderPath,
        parent_path: newSubfolderParentPath,
      })
      .eq("id", subfolder.id);

    if (subfolderError) {
      throw subfolderError;
    }
  }

  // Update all files in this folder and subfolders
  const { error: filesError } = await supabase
    .from("media_metadata")
    .update({ folder_path: newPath })
    .eq("folder_path", folder.path);

  if (filesError) {
    throw filesError;
  }

  // Update files in subfolders
  for (const subfolder of subfolders || []) {
    const newSubfolderPath = subfolder.path.replace(folder.path, newPath);
    const { error: subfolderFilesError } = await supabase
      .from("media_metadata")
      .update({ folder_path: newSubfolderPath })
      .eq("folder_path", subfolder.path);

    if (subfolderFilesError) {
      throw subfolderFilesError;
    }
  }
}

/**
 * Get folder breadcrumb path
 */
export async function getFolderBreadcrumb(
  folderPath: string
): Promise<Folder[]> {
  if (!folderPath) return [];

  const pathParts = folderPath.split("/");
  const folders: Folder[] = [];

  for (let i = 0; i < pathParts.length; i++) {
    const currentPath = pathParts.slice(0, i + 1).join("/");

    const { data, error } = await supabase
      .from("folders")
      .select("*")
      .eq("path", currentPath)
      .single();

    if (error || !data) continue;

    folders.push(data);
  }

  return folders;
}

/**
 * Get folder file counts (total files and used files)
 * - totalFiles: Count of files directly in the folder (not including subfolders)
 * - usedFiles: Count of files used in posts from the folder and all its subfolders
 */
export async function getFolderFileCounts(
  folderPath: string
): Promise<{ totalFiles: number; usedFiles: number }> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw userError || new Error("User not authenticated");
  }

  // Get both total and used files count using a PL/pgSQL query (includes subfolders for used files)
  const { data: fileCountsResult, error: countsError } = await supabase.rpc(
    "count_used_files_in_folder",
    {
      p_user_id: user.id,
      p_folder_path: folderPath,
    }
  );

  if (countsError) {
    throw countsError;
  }

  return {
    totalFiles: (fileCountsResult as any)?.total_files || 0,
    usedFiles: (fileCountsResult as any)?.used_files || 0,
  };
}

/**
 * Get all folders with file counts for the current user
 */
export async function getFoldersWithCounts(
  parentPath: string = ""
): Promise<FolderWithCounts[]> {
  const folders = await getFolders(parentPath);

  const foldersWithCounts = await Promise.all(
    folders.map(async (folder) => {
      const counts = await getFolderFileCounts(folder.path);
      return {
        ...folder,
        ...counts,
      };
    })
  );

  return foldersWithCounts;
}

/**
 * Get folder file counts including subfolders (both total files and used files include subfolders)
 */
export async function getFolderFileCountsWithSubfolders(
  folderPath: string
): Promise<{ totalFiles: number; usedFiles: number }> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw userError || new Error("User not authenticated");
  }

  // Get both total and used files count using the updated PL/pgSQL query (includes subfolders)
  const { data: fileCountsResult, error: countsError } = await supabase.rpc(
    "count_used_files_in_folder",
    {
      p_user_id: user.id,
      p_folder_path: folderPath,
    }
  );

  if (countsError) {
    throw countsError;
  }

  return {
    totalFiles: (fileCountsResult as any)?.total_files || 0,
    usedFiles: (fileCountsResult as any)?.used_files || 0,
  };
}
